import type { TenantPermissions } from "@/features/auth/lib/api";
import { NAV_PAGE_ROUTES } from "@/components/layout/nav-catalog";

/** Maps page permission slugs (bare) to frontend routes — owned by nav-catalog. */
const PAGE_ROUTES: Record<string, string> = NAV_PAGE_ROUTES;

export const BETA_FEATURE_PAGE_PREFIX = "beta-feature.";
export const BETA_FEATURES_PATH_PREFIX = "/beta-features/";

/** One kebab-case segment, e.g. `campaign-analyzer`. Nested slugs are not supported. */
const BETA_FEATURE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function isBetaFeatureSlug(featureSlug: string): boolean {
  return BETA_FEATURE_SLUG_PATTERN.test(featureSlug);
}

export function isBetaFeaturePageSlug(slug: string): boolean {
  if (!slug.startsWith(BETA_FEATURE_PAGE_PREFIX)) return false;
  return isBetaFeatureSlug(slug.slice(BETA_FEATURE_PAGE_PREFIX.length));
}

/** PAGE `beta-feature.*` and ACTION `/beta-features/*` routes — used to keep Beta Features dark in presets. */
export function isBetaFeaturePermissionRoute(route: string): boolean {
  return route.startsWith(BETA_FEATURE_PAGE_PREFIX) || route.startsWith(BETA_FEATURES_PATH_PREFIX);
}

/** Get the frontend route for a page slug, or null if unknown */
export function pageSlugToRoute(slug: string): string | null {
  if (PAGE_ROUTES[slug]) return PAGE_ROUTES[slug];
  if (isBetaFeaturePageSlug(slug)) {
    return `${BETA_FEATURES_PATH_PREFIX}${slug.slice(BETA_FEATURE_PAGE_PREFIX.length)}`;
  }
  return null;
}

/** Get the page slug for a frontend route path, or null if unknown */
export function routeToPageSlug(route: string): string | null {
  for (const [slug, path] of Object.entries(PAGE_ROUTES)) {
    if (path === route) return slug;
  }
  if (route.startsWith(BETA_FEATURES_PATH_PREFIX)) {
    const featureSlug = route.slice(BETA_FEATURES_PATH_PREFIX.length);
    if (!isBetaFeatureSlug(featureSlug)) return null;
    return `${BETA_FEATURE_PAGE_PREFIX}${featureSlug}`;
  }
  return null;
}

/**
 * Build a map of page slug → tenant list.
 * e.g. { 'identity-hub': [{ id: 'x', name: 'Adge' }] }
 */
export function getPageTenantsMap(
  tenants: TenantPermissions[],
): Record<string, { id: string; name: string }[]> {
  const map: Record<string, { id: string; name: string }[]> = {};
  for (const t of tenants) {
    for (const perm of t.permissions) {
      if (perm.type !== "PAGE") continue;
      if (!map[perm.route]) map[perm.route] = [];
      // Avoid duplicate tenants for the same page
      if (!map[perm.route].some((e) => e.id === t.id)) {
        map[perm.route].push({ id: t.id, name: t.name });
      }
    }
  }
  return map;
}

/** Collect all unique PAGE routes the user can access across all tenants */
function getAllPageRoutes(tenants: TenantPermissions[]): string[] {
  const seen = new Set<string>();
  const routes: string[] = [];
  for (const t of tenants) {
    for (const perm of t.permissions) {
      if (perm.type !== "PAGE") continue;
      const route = pageSlugToRoute(perm.route);
      if (route && !seen.has(route)) {
        seen.add(route);
        routes.push(route);
      }
    }
  }
  return routes;
}

/**
 * Retool Workspace remains a valid Explore page, but is no longer a login home.
 * Existing `nexus_last_route=/workspace` values would otherwise keep sending
 * users back to Retool after Dashboard became the default landing page.
 */
const LEGACY_LAST_VISITED_ROUTES = new Set(["/workspace"]);

function shouldHonorLastVisitedRoute(
  lastVisitedRoute: string,
  availableRoutes: string[],
): boolean {
  if (!availableRoutes.includes(lastVisitedRoute)) return false;
  if (!LEGACY_LAST_VISITED_ROUTES.has(lastVisitedRoute)) return true;
  return !availableRoutes.includes("/dashboard");
}

/**
 * Resolve the best route to redirect to after login.
 * Returns null when the user has no accessible pages.
 * The /login beforeLoad must NOT redirect back to /login when null is returned
 * (doing so causes infinite recursion); other callers may safely fall back to /login.
 */
export function getDefaultRoute(
  tenants: TenantPermissions[],
  lastVisitedRoute?: string | null,
): string | null {
  const availableRoutes = getAllPageRoutes(tenants);
  if (availableRoutes.length === 0) return null;

  if (lastVisitedRoute && shouldHonorLastVisitedRoute(lastVisitedRoute, availableRoutes)) {
    return lastVisitedRoute;
  }

  // Prefer /dashboard as landing page when available
  if (availableRoutes.includes("/dashboard")) return "/dashboard";

  return availableRoutes[0];
}
